/** Карточка операции и её правка.
 *
 * В чате это четыре разных состояния FSM и фраза, которую надо угадать: «нет, это
 * был доход», «это были рубли». Здесь — поля. Тип записи меняется сегментом, дата
 * полем: без правки даты трата, записанная на день позже, навсегда останется в
 * чужом месяце, а месяц решает, уложился человек в лимит.
 *
 * ВСЯ ПРАВКА КОПИТСЯ ДО «СОХРАНИТЬ». Тип и категория раньше отправлялись прямо
 * по нажатию на чип, а ответ закрывал шторку — человек тыкал категорию и вылетал
 * назад, не успев поправить название. Теперь чипы меняют черновик, а на сервер
 * уходит одна правка со всеми полями сразу.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type CategoryOut, type Operation, type OperationPatch } from "../lib/api.ts";
import { dayLabel } from "../lib/group.ts";
import { formatMoney } from "../lib/money.ts";
import { tap } from "../lib/telegram.ts";
import { CategoryIcon } from "./CategoryIcon.tsx";
import { Sheet } from "./Sheet.tsx";
import { SheetRow } from "./ui.tsx";

const TYPES: { key: Operation["op_type"]; label: string }[] = [
  { key: "expense", label: "Расход" },
  { key: "income", label: "Доход" },
  { key: "refund", label: "Возврат" },
];
const CURRENCIES = ["VND", "USD", "RUB"];

export function OperationSheet({ operation, onClose, onChanged, onDeleted, onRepeated }: {
  operation: Operation;
  onClose: () => void;
  onChanged: (op: Operation) => void;
  onDeleted: (op: Operation) => void;
  onRepeated: (op: Operation) => void;
}) {
  const [op, setOp] = useState(operation);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(op.amount);
  const [description, setDescription] = useState(op.description);
  const [day, setDay] = useState(op.occurred_at.slice(0, 10));
  // Тип и категория — тоже черновик, а не мгновенное действие.
  const [opType, setOpType] = useState(op.op_type);
  const [category, setCategory] = useState(op.category_name);
  const [currency, setCurrency] = useState(op.currency);
  const [categories, setCategories] = useState<CategoryOut[]>([]);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  useEffect(() => {
    // Список зависит от ЧЕРНОВИКА типа: сменил «расход» на «доход» — и категории
    // должны обновиться сразу, не дожидаясь сохранения.
    if (editing) api.categories(opType === "income" ? "income" : "expense").then(setCategories).catch(() => undefined);
  }, [editing, opType]);

  const apply = (patch: OperationPatch) => {
    if (busy) return;
    tap();
    setBusy(true);
    setTrouble("");
    api
      .patchOperation(op.id, patch)
      .then((next) => { setOp(next); onChanged(next); })
      .catch((e) =>
        setTrouble(
          e instanceof ApiError && e.kind === "unclear"
            ? "Сумму лучше цифрами: 300к, 1.2 млн, 45."
            : e instanceof ApiError && e.kind === "gone"
              ? "Записи больше нет — возможно, её удалили из чата."
              : "Не получилось сохранить. Попробуй ещё раз.",
        ),
      )
      .finally(() => setBusy(false));
  };

  /** Начать правку с чистого черновика: прошлые незавершённые изменения
   *  не должны всплыть при следующем открытии. */
  const startEditing = () => {
    setAmount(op.amount);
    setDescription(op.description);
    setDay(op.occurred_at.slice(0, 10));
    setOpType(op.op_type);
    setCategory(op.category_name);
    setCurrency(op.currency);
    setTrouble("");
    setEditing(true);
  };

  const save = () => {
    if (busy) return;
    const patch: OperationPatch = {};
    if (amount !== op.amount) patch.amount = amount;
    if (description !== op.description) patch.description = description;
    if (opType !== op.op_type) patch.op_type = opType;
    if (currency !== op.currency) patch.currency = currency;
    // Категорию шлём, только если она есть в списке ДЛЯ ВЫБРАННОГО типа: после
    // смены «расход → доход» старая категория к новому типу не относится, и
    // отправить её значило бы попросить сервер о несуществующем сочетании.
    if (category !== op.category_name && categories.some((c) => c.name === category)) {
      patch.category = category;
    }
    // Шлём КАЛЕНДАРНЫЙ день без времени: часовой пояс продукта знает сервер,
    // а наивная строка «12:00» уехала бы в таймзону серверного процесса.
    if (day !== op.occurred_at.slice(0, 10)) patch.occurred_on = day;
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    apply(patch);
    setEditing(false);
  };

  // Повтор — самое частое действие в этом шите: пять одних и тех же фраз дают
  // треть всех записей. Поэтому он отдельной кнопкой, а не внутри «Изменить».
  const again = () => {
    if (busy) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.repeatOperation(op.id).then(onRepeated)
      .catch(() => setTrouble("Не получилось повторить. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    if (busy) return;
    tap();
    setBusy(true);
    api.deleteOperation(op.id).then(onDeleted).catch(() => setTrouble("Не получилось удалить. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={editing ? "Изменить" : op.description || op.category_name} onClose={onClose}>
      {!editing && (
        <div style={{ textAlign: "center", marginBottom: "var(--s-5)" }}>
          <div className="num" style={{ fontSize: "var(--f-t1)", fontWeight: 700,
                                        letterSpacing: "-0.8px",
                                        color: op.op_type === "income" ? "var(--income)" : "var(--text)" }}>
            {op.op_type === "income" ? "+" : "−"}{formatMoney(op.amount_base, op.base_currency)}
          </div>
          {op.currency !== op.base_currency && (
            <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                          marginTop: 4 }}>
              <span className="num">{formatMoney(op.amount, op.currency)}</span>
              {/* Курс историчен и не переписывается (INV-6). Дата отвечает на
                  единственный вопрос, который тут возникает: почему сумма в
                  базовой не сходится с сегодняшним курсом. */}
              {op.fx_date && op.fx_date !== op.occurred_at.slice(0, 10)
                ? ` · курс на ${dayLabel(op.fx_date)}`
                : null}
            </div>
          )}
        </div>
      )}

      {editing ? (
        <>
          <Field label="Сумма">
            <input value={amount} onChange={(e) => setAmount(e.target.value)}
                   inputMode="decimal" style={input} aria-label="Сумма" />
          </Field>
          <Chips options={CURRENCIES} active={currency}
                 onPick={(c) => { tap(); setCurrency(c); }} />
          <Field label="Название">
            <input value={description} onChange={(e) => setDescription(e.target.value)}
                   maxLength={200} style={input} aria-label="Название" />
          </Field>
          <Field label="Дата">
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
                   style={input} aria-label="Дата" />
          </Field>
          <Field label="Тип">
            <Chips options={TYPES.map((t) => t.label)} active={TYPES.find((t) => t.key === opType)!.label}
                   onPick={(label) => { tap(); setOpType(TYPES.find((t) => t.label === label)!.key); }} />
          </Field>
          {categories.length > 0 && (
            <Field label="Категория">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)" }}>
                {categories.map((c) => (
                  <Chip key={c.id} label={c.name} active={c.name === category}
                        icon={<CategoryIcon name={c.name} color="var(--text-muted)" size={22} />}
                        onPick={() => { tap(); setCategory(c.name); }} />
                ))}
              </div>
            </Field>
          )}
        </>
      ) : (
        <div style={{ background: "var(--surface)", borderRadius: "var(--r-card)",
                      padding: "2px var(--s-4)" }}>
          <SheetRow label="Тип" value={TYPES.find((t) => t.key === op.op_type)?.label ?? op.op_type} />
          <SheetRow label="Категория" value={op.category_name} />
          <SheetRow label="Дата" value={formatDay(op.occurred_at)} />
          <SheetRow label="Валюта" value={op.currency} last />
        </div>
      )}

      {trouble && (
        <p role="alert" style={{ fontSize: "var(--f-caption)", color: "var(--warn)", lineHeight: 1.5,
                    marginTop: "var(--s-3)" }}>{trouble}</p>
      )}

      {!editing && (
        <button type="button" aria-disabled={busy || undefined} onClick={again}
                style={{ ...button, width: "100%", marginTop: "var(--s-5)",
                         background: "var(--accent)", color: "var(--on-accent)" }}>
          Повторить сегодня
        </button>
      )}

      <div style={{ display: "flex", gap: "var(--s-3)", marginTop: "var(--s-3)" }}>
        <button type="button" aria-disabled={busy || undefined} onClick={() => (editing ? save() : startEditing())}
                style={{ ...button, flexGrow: 1,
                         background: editing ? "var(--accent)" : "var(--surface-2)",
                         color: editing ? "var(--on-accent)" : "var(--text)" }}>
          {editing ? "Сохранить" : "Изменить"}
        </button>
        <button type="button" aria-disabled={busy || undefined} onClick={editing ? () => setEditing(false) : remove}
                style={{ ...button, flexGrow: 1,
                         background: editing ? "var(--surface-2)" : "transparent",
                         color: editing ? "var(--text)" : "var(--danger)",
                         border: editing ? "none" : "1px solid color-mix(in srgb, var(--danger) 30%, transparent)" }}>
          {editing ? "Отмена" : "Удалить"}
        </button>
      </div>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: "var(--s-4)" }}>
      <span style={{ display: "block", fontSize: "var(--f-caption)", color: "var(--text-muted)",
                     marginBottom: "var(--s-2)" }}>{label}</span>
      {children}
    </label>
  );
}

function Chips({ options, active, onPick }: {
  options: string[]; active: string; onPick: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: "var(--s-2)", marginBottom: "var(--s-4)" }}>
      {options.map((o) => (
        <Chip key={o} label={o} active={o === active} onPick={() => onPick(o)} grow />
      ))}
    </div>
  );
}

function Chip({ label, active, onPick, grow, icon }: {
  label: string; active: boolean; onPick: () => void; grow?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onPick} aria-pressed={active}
            style={{ flexGrow: grow ? 1 : 0, minHeight: 38, padding: "8px 14px",
                     borderRadius: "var(--r-sm)", cursor: "pointer", font: "inherit",
                     fontSize: "var(--f-body-s)", fontWeight: active ? 600 : 400,
                     background: active ? "var(--accent)" : "var(--surface-2)",
                     color: active ? "var(--on-accent)" : "var(--text)",
                     border: "none", display: "inline-flex", alignItems: "center",
                     gap: icon ? 6 : 0 }}>
      {icon}
      {label}
    </button>
  );
}


const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function formatDay(iso: string): string {
  const [, month, date] = iso.slice(0, 10).split("-");
  return `${Number(date)} ${MONTHS[Number(month) - 1]}`;
}

const input: React.CSSProperties = {
  width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)",
  border: "none", borderRadius: "var(--r-field)", padding: "12px 14px",
  color: "var(--text)", font: "inherit", fontSize: "var(--f-body)",
};

const button: React.CSSProperties = {
  minHeight: "var(--tap)", padding: "14px", borderRadius: "var(--r-field)",
  border: "none", cursor: "pointer", font: "inherit",
  fontSize: "var(--f-body)", fontWeight: 600,
};
